import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, errMessage } from "@/portal/api";
import { useAuth } from "@/portal/AuthContext";
import { Card } from "@/pages/portal/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { cn } from "@/lib/utils";
import { Loader2, Plus, X, AlertCircle } from "lucide-react";
import { toast } from "sonner";

const SERVICE_LABELS = { electricity: "Commercial Electricity", gas: "Commercial Natural Gas", battery: "Virtual Power Plant Network" };

export default function NewApplication() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [elig, setElig] = useState(null);
  const [reps, setReps] = useState([]);
  const [busy, setBusy] = useState(false);

  const [form, setForm] = useState({
    company_name: "", dm_name: "", dm_title: "", dm_email: "", dm_phone: "",
    state: "", agreement_expiration: "", notes: "", rep_id: "",
  });
  const [services, setServices] = useState([]);
  const [files, setFiles] = useState([]); // {file, label}

  useEffect(() => {
    api.get("/api/portal/eligibility").then((r) => setElig(r.data)).catch((e) => toast.error(errMessage(e)));
    if (["admin", "manager", "rep"].includes(user.role)) {
      api.get("/api/portal/reps").then((r) => {
        setReps(r.data.reps);
        setForm((f) => ({ ...f, rep_id: user.id }));
      }).catch(() => {});
    }
  }, [user.role, user.id]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const stateInfo = elig && form.state ? elig.eligibility[form.state] : null;
  const available = stateInfo?.available || [];

  const onStateChange = (v) => {
    set("state", v);
    setServices([]);
  };
  const toggleService = (s) => {
    setServices((prev) => (prev.includes(s) ? prev.filter((x) => x !== s) : [...prev, s]));
  };

  const addFile = (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (f) setFiles((prev) => [...prev, { file: f, label: "electric_bill" }]);
  };
  const setFileLabel = (i, label) => setFiles((prev) => prev.map((x, idx) => (idx === i ? { ...x, label } : x)));
  const removeFile = (i) => setFiles((prev) => prev.filter((_, idx) => idx !== i));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.state) return toast.error("Please select a state.");
    if (services.length === 0) return toast.error("Please select at least one service.");
    setBusy(true);
    try {
      const payload = {
        company_name: form.company_name,
        decision_maker: { name: form.dm_name, title: form.dm_title, email: form.dm_email, phone: form.dm_phone },
        state: form.state,
        services,
        agreement_expiration: form.agreement_expiration || null,
        notes: form.notes,
      };
      if (["admin", "manager", "rep"].includes(user.role) && form.rep_id) payload.rep_id = form.rep_id;
      const { data } = await api.post("/api/portal/applications", payload);
      const appId = data.application.id;
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f.file);
        fd.append("label", f.label);
        try { await api.post(`/api/portal/applications/${appId}/files`, fd); } catch { /* continue */ }
      }
      toast.success("Application submitted");
      navigate(user.role === "customer" ? "/portal" : `/portal/applications/${appId}`);
    } catch (err) {
      toast.error(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const states = elig ? Object.entries(elig.states).sort((a, b) => a[1].localeCompare(b[1])) : [];

  return (
    <div className="mx-auto max-w-2xl" data-testid="new-application">
      <h2 className="mb-1 font-heading text-2xl font-semibold text-navy">
        {user.role === "customer" ? "New application" : "Submit a potential customer"}
      </h2>
      <p className="mb-6 text-sm text-slatey">Provide the details below. Services are filtered by what's available in the selected state.</p>

      <form onSubmit={submit} className="space-y-6">
        <Card className="space-y-4 p-5">
          <div>
            <Label className="text-navy">Business name</Label>
            <Input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} className="mt-1.5 bg-white" data-testid="field-company" />
          </div>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-navy">State</Label>
              <select required value={form.state} onChange={(e) => onStateChange(e.target.value)} data-testid="field-state" className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-navy">
                <option value="">Select a state…</option>
                {states.map(([abbr, name]) => (
                  <option key={abbr} value={abbr}>{name}</option>
                ))}
              </select>
            </div>
            <div>
              <Label className="text-navy">Existing agreement expiration</Label>
              <Input type="date" value={form.agreement_expiration} onChange={(e) => set("agreement_expiration", e.target.value)} className="mt-1.5 bg-white" data-testid="field-expiration" />
            </div>
          </div>

          {/* Eligibility-driven services */}
          {form.state && (
            <div>
              <Label className="text-navy">Services of interest</Label>
              {available.length === 0 ? (
                <div className="mt-2 flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-3 text-sm text-amber-800" data-testid="no-services">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />
                  We don't currently serve {stateInfo?.name}. Please choose another state or contact us for options.
                </div>
              ) : (
                <div className="mt-2 space-y-2" data-testid="service-options">
                  {available.map((s) => (
                    <label key={s} className={cn("flex cursor-pointer items-center gap-3 rounded-md border p-3 text-sm transition-colors", services.includes(s) ? "border-electric bg-electric/5" : "border-line bg-white hover:border-navy/30")}>
                      <input type="checkbox" checked={services.includes(s)} onChange={() => toggleService(s)} data-testid={`service-${s}`} className="h-4 w-4 accent-electric" />
                      <span className="font-medium text-navy">{SERVICE_LABELS[s]}</span>
                      {stateInfo?.notes?.[s] && <span className="ml-auto text-xs text-slatey">{stateInfo.notes[s]}</span>}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}
        </Card>

        <Card className="space-y-4 p-5">
          <h3 className="font-heading text-sm font-semibold text-navy">Decision maker</h3>
          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-navy">Name</Label>
              <Input required value={form.dm_name} onChange={(e) => set("dm_name", e.target.value)} className="mt-1.5 bg-white" data-testid="field-dm-name" />
            </div>
            <div>
              <Label className="text-navy">Title</Label>
              <Input value={form.dm_title} onChange={(e) => set("dm_title", e.target.value)} className="mt-1.5 bg-white" data-testid="field-dm-title" />
            </div>
            <div>
              <Label className="text-navy">Email</Label>
              <Input type="email" required value={form.dm_email} onChange={(e) => set("dm_email", e.target.value)} className="mt-1.5 bg-white" data-testid="field-dm-email" />
            </div>
            <div>
              <Label className="text-navy">Phone</Label>
              <Input required value={form.dm_phone} onChange={(e) => set("dm_phone", e.target.value)} className="mt-1.5 bg-white" data-testid="field-dm-phone" />
            </div>
          </div>
        </Card>

        <Card className="space-y-4 p-5">
          <div className="flex items-center justify-between">
            <h3 className="font-heading text-sm font-semibold text-navy">Bills & statements</h3>
            <label className="inline-flex cursor-pointer items-center gap-1.5 rounded-md border border-line bg-white px-3 py-1.5 text-xs font-medium text-navy hover:border-navy/40" data-testid="add-file-btn">
              <Plus className="h-3.5 w-3.5" /> Add file
              <input type="file" className="hidden" onChange={addFile} accept=".pdf,.png,.jpg,.jpeg,.webp,.heic" />
            </label>
          </div>
          {files.length === 0 ? (
            <p className="text-xs text-slatey">Upload the electric and/or gas bill. You can add multiple and label each.</p>
          ) : (
            <ul className="space-y-2" data-testid="file-staging">
              {files.map((f, i) => (
                <li key={i} className="flex items-center gap-2 rounded-md border border-line bg-ice p-2">
                  <span className="min-w-0 flex-1 truncate text-sm text-navy">{f.file.name}</span>
                  <select value={f.label} onChange={(e) => setFileLabel(i, e.target.value)} className="rounded border border-line bg-white px-2 py-1 text-xs text-navy">
                    <option value="electric_bill">Electric bill</option>
                    <option value="gas_bill">Gas bill</option>
                    <option value="other_bill">Other</option>
                  </select>
                  <button type="button" onClick={() => removeFile(i)} className="text-slatey hover:text-red-600"><X className="h-4 w-4" /></button>
                </li>
              ))}
            </ul>
          )}
        </Card>

        <Card className="space-y-4 p-5">
          {["admin", "manager", "rep"].includes(user.role) && reps.length > 0 && (
            <div>
              <Label className="text-navy">Assign to rep</Label>
              <select value={form.rep_id} onChange={(e) => set("rep_id", e.target.value)} data-testid="field-rep" className="mt-1.5 w-full rounded-md border border-line bg-white px-3 py-2 text-sm text-navy">
                {reps.map((r) => (
                  <option key={r.id} value={r.id}>{r.name}{r.id === user.id ? " (me)" : ""}</option>
                ))}
              </select>
            </div>
          )}
          <div>
            <Label className="text-navy">Notes</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} rows={3} className="mt-1.5 bg-white" data-testid="field-notes" placeholder="Anything else we should know?" />
          </div>
        </Card>

        <Button type="submit" disabled={busy} className="w-full bg-electric text-white hover:bg-electric-hover" data-testid="submit-application">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Submit application"}
        </Button>
      </form>
    </div>
  );
}
